import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { useToast } from '@/components/feedback/toast';
import { routes } from '@/config/routes';
import { AUTH_ERROR_CODES, AUTH_ERROR_COPY } from '@/features/auth';
import { onForceLogout } from '@/lib/api/force-logout';
import { useAuth } from '@/providers/auth-provider';

/**
 * Mounted above every route — public and protected alike — so a force-logout (US-112329's
 * `ACCOUNT_DEACTIVATED`, or a refresh that couldn't rescue the session) always clears the session
 * and lands the grower on Login, no matter which surface they were on.
 *
 * `<AppShell>` used to own this subscription, which left every route outside it — Login, the OTP
 * screens, and the post-login **Select Account** screen chief among them — with no listener at
 * all: a dead refresh token there just toasted "Refresh failed" and stranded the grower in place,
 * since `<AppShell>` (and the subscription with it) was never mounted. Moving the one subscription
 * here instead of also wiring it into every public page individually keeps it singular.
 */
export function RootLayout() {
  const { clearSession } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(
    () =>
      onForceLogout((reason) => {
        clearSession();
        navigate(routes.login, { replace: true });

        if (reason.kind === 'deactivated') {
          toast.error({ title: AUTH_ERROR_COPY[AUTH_ERROR_CODES.ACCOUNT_DEACTIVATED] });
          return;
        }

        toast.warning({
          title: reason.message || AUTH_ERROR_COPY[AUTH_ERROR_CODES.SESSION_EXPIRED],
        });
      }),
    [clearSession, navigate, toast],
  );

  return <Outlet />;
}
